
import { jitsiLocalStorage } from '@jitsi/js-utils';

import { MEDIA_TYPE } from '../base/media/constants';
import { getRemoteParticipants } from '../base/participants/functions';

import {
    DEFAULT_INWORLD_BASE_URL,
    DEFAULT_TTS_LANGUAGES,
    MIN_SUBTITLES_FONT_SIZE,
    SYNTHETIC_TRANSLATED_SOURCE_MARKER,
    VOICE_CLONE_ID_KEY,
    VOICE_CLONE_SELECTED_KEY
} from './constants';


/**
 * Logs when about the received transcription chunk.
 *
 * @param {string} transcriptMessageID - Transcription message id.
 * @param {string} language - The language of the transcribed message.
 * @param {Object} participant - The participant who send the message.
 * @param {any} text - The message text.
 * @param {any} _store - The store.
 * @returns {Event}
 */
export const notifyTranscriptionChunkReceived = ({ transcriptMessageID, language, participant, text }) =>
    APP.API.notifyTranscriptionChunkReceived({
        messageID: transcriptMessageID,
        language,
        participant,
        ...text
    });


/**
 * Calculates the font size for the subtitles.
 *
 * @param {number} clientHeight - The height of the visible area of the window.
 * @returns {number}
 */
export function calculateSubtitlesFontSize(clientHeight) {
    if (typeof clientHeight === 'undefined') {
        return MIN_SUBTITLES_FONT_SIZE;
    }

    return Math.max(Math.floor(clientHeight * 0.04), MIN_SUBTITLES_FONT_SIZE);
}

/**
 * Builds a { participants: [ { ...participant, translatedTracks } ] } view
 * over the remote participants, merging in each participant's translated-
 * voice audio track entries (one per target language) tracked in the
 * subtitles feature state.
 *
 * Note this is a read-only projection for UI consumption. The authoritative
 * per-participant translated track data lives on the MHParticipant instance
 * itself (see MHParticipant#getTranslatedTracks in lib-meet-hour), kept in
 * sync by the subtitles middleware as translated-audio-track endpoint
 * messages arrive; this selector mirrors that state for components that read
 * from redux rather than reaching into the conference object.
 *
 * @param {Object} state - The redux state.
 * @returns {{ participants: Array<Object> }}
 */
export function getRemoteParticipantsWithTranslatedTracks(state) {
    const remoteParticipants = getRemoteParticipants(state);
    const byParticipant = state['features/subtitles']._translatedAudioTracksByParticipant;

    const participants = remoteParticipants.map(participant => {
        const tracksByLanguage = byParticipant.get(participant.id);
        const translatedTracks = tracksByLanguage
            ? Array.from(tracksByLanguage.values()).map(track => {
                return {
                    trackOpus: track.trackOpus,
                    translateLanguage: track.language,
                    active: Boolean(track.active)
                };
            })
            : [];

        return {
            ...participant,
            translatedTracks
        };
    });

    return { participants };
}

/**
 * Finds a participant's ORIGINAL (non-synthetic) audio track specifically, not just "a" audio track for them:
 * once their synthetic per-language translated track also exists (see subtitles/subscriber.js), both share the
 * same participantId, so a plain "the audio track for this participant" lookup (e.g.
 * getTrackByMediaTypeAndParticipant, which prefers unmuted) could arbitrarily return the synthetic one instead.
 * Callers that need this participant's real microphone track specifically (mute state, connection stats, source
 * name for excluding it from a subscription) should use this instead of a generic lookup.
 *
 * @param {Array<Object>} tracks - `state['features/base/tracks']`.
 * @param {string} participantId - The track-owning participant's id.
 * @returns {Object|undefined}
 */
export function getOriginalAudioTrackByParticipant(tracks, participantId) {
    return tracks.find(t =>
        t.mediaType === MEDIA_TYPE.AUDIO
        && t.participantId === participantId
        && Boolean(t.mhTrack)
        && !t.mhTrack.getSourceName?.()?.includes(SYNTHETIC_TRANSLATED_SOURCE_MARKER));
}

/**
 * Whether a remote participant's ORIGINAL microphone is muted - like {@code isRemoteTrackMuted(tracks,
 * MEDIA_TYPE.AUDIO, participantId)}, but immune to the same-participantId collision described on
 * {@link getOriginalAudioTrackByParticipant}. Use this instead of that generic call wherever the audio-muted
 * state feeds a mic-muted icon or a mute/unmute button/label for a remote participant.
 *
 * @param {Array<Object>} tracks - `state['features/base/tracks']`.
 * @param {string} participantId - The track-owning participant's id.
 * @returns {boolean}
 */
export function isRemoteAudioTrackMuted(tracks, participantId) {
    const track = getOriginalAudioTrackByParticipant(tracks, participantId);

    return !track || track.muted;
}

/**
 * Returns the local listener's currently-selected translation language (the
 * same target language driving both captions and, when a speaker is in
 * translated voice mode, what Jigasi synthesizes speech into), stripped of
 * the `translation-languages:` prefix used internally in redux/participant
 * properties. Undefined if the listener hasn't requested a translation
 * language.
 *
 * @param {Object} state - The redux state.
 * @returns {string|undefined}
 */
export function getSelectedTranslationLanguage(state) {
    const raw = state['features/subtitles']._language;

    return raw ? raw.replace('translation-languages:', '') : undefined;
}

/**
 * The base (two-letter) form of a language tag, so `en-US`, `en_GB` and `en` all compare equal. Jigasi and
 * LibreTranslate both key off the two-letter code, so this is the granularity at which "same language" means
 * "nothing to translate".
 *
 * @param {string|undefined} language - A language tag.
 * @returns {string|undefined}
 */
function baseLanguage(language) {
    const trimmed = language?.trim();

    return trimmed ? trimmed.substring(0, 2).toLowerCase() : undefined;
}

/**
 * The conference's transcription (source) language - the language participants are assumed to be speaking, set in
 * config and shown at the top of the transcription dialog.
 *
 * @param {Object} state - The redux state.
 * @returns {string|undefined}
 */
export function getTranscriptionLanguage(state) {
    return state['features/base/conference']?.conference?.options?.config?.transcriptionLanguage;
}

/**
 * The translation language the local listener can actually *use* for translated voice: their selected language,
 * when the voice provider speaks it. Deliberately allowed even when it is the language being spoken: the spoken
 * language is recognized per sentence, and a listener who turned translated voice on hears every sentence voiced -
 * translated where needed, the original words otherwise - in one consistent voice and order. Undefined means
 * "translated voice is not available to this listener", which both the subscription and the toggle key off.
 *
 * @param {Object} state - The redux state.
 * @returns {string|undefined}
 */
export function getEffectiveTranslationLanguage(state) {
    return getTranslatedVoiceUnavailableReason(state) ? undefined : getSelectedTranslationLanguage(state);
}

/**
 * The languages translated voice can be synthesized into, from config when set (see
 * {@code config.transcription.ttsLanguages}) and otherwise {@link DEFAULT_TTS_LANGUAGES}.
 *
 * @param {Object} state - The redux state.
 * @returns {Array<string>}
 */
function getTtsLanguages(state) {
    const configured = state['features/base/config']?.transcription?.ttsLanguages;

    return Array.isArray(configured) && configured.length ? configured : DEFAULT_TTS_LANGUAGES;
}

/**
 * Why translated voice cannot be used right now, or `undefined` when it can. Drives both the disabled state of the
 * toggle in the transcription dialog and the tooltip explaining it, so the reason shown is always the reason the
 * feature is actually withheld.
 *
 * @param {Object} state - The redux state.
 * @param {string|undefined} candidateLanguage - A language to judge instead of the one currently in state. Used
 * when a language has just been picked and the answer is needed before the store has caught up.
 * @returns {string|undefined} one of `noLanguage`, `unsupportedLanguage`.
 */
export function getTranslatedVoiceUnavailableReason(state, candidateLanguage) {
    const selected = candidateLanguage === undefined
        ? getSelectedTranslationLanguage(state)
        : candidateLanguage?.replace('translation-languages:', '');

    if (!selected) {
        return 'noLanguage';
    }

    if (!getTtsLanguages(state).some(lang => baseLanguage(lang) === baseLanguage(selected))) {
        return 'unsupportedLanguage';
    }

    return undefined;
}

/**
 * The voice the local listener hears a remote speaker in: 'original' or 'translated'.
 *
 * A moderator's forced mode for that speaker applies to every listener, so it wins. Otherwise this follows the
 * listener's own translated-voice toggle in the transcription dialog, which applies to every speaker alike and -
 * crucially - is never published as anyone else's state, so it cannot change what another listener hears. The
 * forced flag is checked under both names in use: `force`, which Jigasi's own broadcasts carry, and
 * `moderator_forced`, which this client's optimistic local update writes.
 *
 * @param {Map<string, Object>} voiceModeByParticipant -
 * `state['features/subtitles']._voiceModeByParticipant`.
 * @param {boolean} translatedVoiceEnabled -
 * `state['features/subtitles']._translatedVoiceEnabled`.
 * @param {string} participantId - The speaker's participant id.
 * @returns {string}
 */
export function getListenerVoiceMode(voiceModeByParticipant, translatedVoiceEnabled, participantId) {
    const roomState = voiceModeByParticipant?.get?.(participantId);

    if (roomState?.force || roomState?.moderator_forced) {
        return roomState.effective_voice_mode?.toLowerCase();
    }

    return translatedVoiceEnabled ? 'translated' : 'original';
}

/**
 * Determines whether a remote audio track should be muted for the local
 * listener because it carries legacy shared-SSRC translated-voice audio
 * (see INWORLD_TTS_IMPLEMENTATION_REFERENCE.md §3.4/§9) - a single shared
 * transcriber source that used to be unmuted/muted based on which speaker+
 * language was currently active for the listener.
 *
 * Always returns `true` for a recognized legacy track: Jigasi now also sends
 * the same synthesized audio as a per-speaker+language synthetic source (see
 * TranslationOrchestrator/mediajson), which this listener subscribes to
 * separately when it matches their selected language (see subtitles/
 * subscriber.js). Unmuting the legacy track too would play the same
 * translation twice. The legacy `translated-audio-track` announcements are
 * still tracked (for e.g. UI "who's being translated" state) - this function
 * just no longer lets that track itself become audible.
 *
 * Returns `undefined` - meaning "not a translated-audio track, leave the
 * normal default behavior alone" - for any track that was never announced
 * via a `translated-audio-track` endpoint message, so ordinary participant
 * microphone tracks are never affected by this gating.
 *
 * @param {Map<string, Map<string, Object>>} translatedAudioTracksByParticipant -
 * `state['features/subtitles']._translatedAudioTracksByParticipant`.
 * @param {string|undefined} selectedLanguage - Unused; kept for call-site compatibility.
 * @param {string|undefined} sourceName - The remote track's source name
 * (`mhTrack.getSourceName()`).
 * @returns {boolean|undefined}
 */
export function computeTranslatedAudioTrackMuted(
        translatedAudioTracksByParticipant, selectedLanguage, sourceName) {
    if (!sourceName || !translatedAudioTracksByParticipant) {
        return undefined;
    }

    let isKnownTranslatedTrack = false;

    for (const tracksByLanguage of translatedAudioTracksByParticipant.values()) {
        for (const entry of tracksByLanguage.values()) {
            if (entry.trackOpus === sourceName) {
                isKnownTranslatedTrack = true;
            }
        }
    }

    if (!isKnownTranslatedTrack) {
        return undefined;
    }

    return true;
}

/**
 * Determines whether a participant's own (original) microphone audio track
 * should be muted for the local listener, because that participant is in
 * translated voice mode and the listener has selected a translation
 * language - i.e. the listener should hear the synthesized translation
 * instead of the original speech, not both.
 *
 * Unlike {@link computeTranslatedAudioTrackMuted}, this does not gate on the
 * translated track's moment-to-moment `active` state: while a participant is
 * in translated mode for a listener who wants translation, their original
 * track stays muted for the whole duration, including the (silent) gaps
 * between synthesized utterances - toggling it back on during those gaps
 * would mean brief bursts of un-translated original speech, which is not the
 * intended UX.
 *
 * Decided from the local listener's own toggle (see {@link getListenerVoiceMode}), not from any shared state, so
 * muting a speaker's original voice here can never follow from another listener's choice.
 *
 * @param {Map<string, Object>} voiceModeByParticipant -
 * `state['features/subtitles']._voiceModeByParticipant`.
 * @param {boolean} translatedVoiceEnabled -
 * `state['features/subtitles']._translatedVoiceEnabled`.
 * @param {string|undefined} effectiveLanguage - The listener's usable translation language (see
 * {@link getEffectiveTranslationLanguage}).
 * @param {string|undefined} participantId - The owning participant's id for
 * the track in question.
 * @returns {boolean|undefined} `undefined` when there isn't enough information to decide (no usable translation
 * language) - meaning "leave the normal default behavior alone".
 */
export function shouldMuteOriginalTrackForTranslatedListener(
        voiceModeByParticipant, translatedVoiceEnabled, effectiveLanguage, participantId) {
    if (!effectiveLanguage || !participantId) {
        return undefined;
    }

    return getListenerVoiceMode(voiceModeByParticipant, translatedVoiceEnabled, participantId) === 'translated';
}

/**
 * Reads the Inworld settings the browser needs to create a voice clone itself.
 *
 * The API key is served to every client, so it is readable by anyone who loads the page. This is an interim
 * arrangement: cloning is done from the browser so no audio has to cross XMPP, and the key should move behind a
 * short-lived token minted server-side before this is offered to real users.
 *
 * @param {Object} state - The redux state.
 * @returns {{apiKey: string, baseUrl: string}|undefined} Undefined when no key is configured.
 */
export function getInworldCloneConfig(state) {
    const { inworld, voiceMode } = state['features/base/config']?.transcription ?? {};

    // The deployed Docker template (shoeb-stable-11146-2) renders the key under voiceMode.
    const settings = inworld?.apiKey ? inworld : voiceMode;

    if (!settings?.apiKey) {
        return undefined;
    }

    return {
        apiKey: settings.apiKey,
        baseUrl: settings.baseUrl || DEFAULT_INWORLD_BASE_URL
    };
}

/**
 * Reads a local storage value, tolerating browsers that block storage entirely.
 *
 * @param {string} key - The key to read.
 * @returns {string|undefined}
 */
function readStoredValue(key) {
    try {
        return jitsiLocalStorage.getItem(key) || undefined;
    } catch {
        return undefined;
    }
}

/**
 * Writes a local storage value, removing it when the value is empty.
 *
 * @param {string} key - The key to write.
 * @param {string|undefined} value - The value, or empty to remove the key.
 * @returns {void}
 */
function writeStoredValue(key, value) {
    try {
        if (value) {
            jitsiLocalStorage.setItem(key, value);
        } else {
            jitsiLocalStorage.removeItem(key);
        }
    } catch {
        // localStorage may be blocked in strict browser contexts.
    }
}

/**
 * @returns {string|undefined} The Inworld voice id cloned from this browser, if there is one.
 */
export function getStoredCloneVoiceId() {
    return readStoredValue(VOICE_CLONE_ID_KEY);
}

/**
 * Remembers the Inworld voice id cloned from this browser.
 *
 * @param {string|undefined} voiceId - The id, or empty to forget it.
 * @returns {void}
 */
export function setStoredCloneVoiceId(voiceId) {
    writeStoredValue(VOICE_CLONE_ID_KEY, voiceId);
}

/**
 * @returns {boolean} Whether the user picked their cloned voice over the default one. Defaults to true, so a
 * clone that was just created is used without a second click.
 */
export function isCloneVoiceSelected() {
    return readStoredValue(VOICE_CLONE_SELECTED_KEY) !== 'false';
}

/**
 * Remembers whether the user picked their cloned voice over the default one.
 *
 * @param {boolean} selected - The choice.
 * @returns {void}
 */
export function setCloneVoiceSelected(selected) {
    writeStoredValue(VOICE_CLONE_SELECTED_KEY, selected ? 'true' : 'false');
}

/**
 * Whether the captions panel is currently shown docked ('fixed') in the video
 * space, taking room from the stage / tile grid.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean}
 */
export function isFixedCaptionsVisible(state) {
    const { _captionsView, _requestingCaptionsModal, _requestingSubtitles } = state['features/subtitles'];

    return Boolean(!window.interfaceConfig?.DISABLE_TRANSCRIPTION_SUBTITLES
        && _requestingSubtitles && _requestingCaptionsModal && _captionsView === 'fixed');
}
